import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { ThreeDot } from "react-loading-indicators";
import { set } from "idb-keyval";
import { usePaths } from "BEDROCK/paths";
import { useTemplateStore } from "entity-store/template-store/use-template-store";
import { useStravaPreviewData } from "../hooks/use-strava-preview-data";
import { PosterProjectProvider } from "PosterEditor/model/poster-project-provider";
import { PosterStage } from "PosterEditor/stage/PosterStage";
import { EditorUIProvider } from "PosterEditor/use-editor-ui";
import { TooltipProvider } from "PosterEditor/tooltips";
import { PosterProject, GPSSource, defaultCoordinateLimit } from "PosterEditor/model/poster-project";
import { ProjectTemplate, applyTemplateToProject } from "PosterEditor/model/project-template";
import { defaultProject, defaultGPSSource } from "PosterEditor/model/default-project-style";
import {
  StravaPreviewData,
  StravaAnalyticsEvents,
  StravaPreviewErrorTypes,
  StravaPreviewCTAActions,
} from "../types";
import { StravaCtaOverlay } from "../components/strava-cta-overlay";
import { ActivitySummaryCard } from "../components/activity-summary-card";
import { useAnalyticsTracker } from "analytics-tracker";
import { reduceCoordinates } from "PosterEditor/hooks/use-coordinates";
import styles from "./strava-preview-page.module.css";

type PreviewState =
  | { status: "loading"; message: string }
  | { status: "ready"; project: PosterProject; template: ProjectTemplate; previewData: StravaPreviewData }
  | { status: "error"; message: string };

/**
 * Strava Preview Page - displays a user's Strava activity on a selected template
 * in read-only mode with a CTA to create an account.
 *
 * Route: /strava/preview/:templateId
 */
export const StravaPreviewPage = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const { navigateTo, auth, collectionList } = usePaths();
  const { fetchTemplate } = useTemplateStore();
  const { getPreviewData } = useStravaPreviewData();
  const { trackEvent } = useAnalyticsTracker();

  const hasInitializedRef = useRef(false);

  const [previewState, setPreviewState] = useState<PreviewState>({
    status: "loading",
    message: "Loading template...",
  });

  /**
   * Save GPS source to IndexedDB for the PosterProjectProvider to load.
   */
  const saveCoordinatesLocal = useCallback(async (gpsSource: GPSSource, id: string) => {
    try {
      await set(`gps-coordinates-${id}`, gpsSource);
    } catch (err) {
      console.error("Failed to save coordinates locally", err);
    }
  }, []);

  /**
   * Create a project from a template with Strava GPS data injected.
   */
  const createProjectFromTemplateWithStravaData = useCallback(
    (template: ProjectTemplate, stravaData: StravaPreviewData): PosterProject => {
      // Start with a fresh project based on defaults
      const project: PosterProject = {
        ...defaultProject,
        id: "guest_project",
        isTemplateDraft: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Apply template styling and elements to the project
      const styledProject = applyTemplateToProject(template, project);

      // Create GPS source ID
      const gpsSourceId = uuid();

      // Reduce coordinates for performance
      const displayedCoordinates = reduceCoordinates(stravaData.coordinates, defaultCoordinateLimit);

      // Create GPS source from Strava data
      const gpsSource: GPSSource = {
        ...defaultGPSSource,
        id: gpsSourceId,
        name: stravaData.activityName,
        originDescription: `Strava: ${stravaData.activityType}`,
        allCoordinates: stravaData.coordinates,
        displayedCoordinates,
        data: stravaData.gpsData,
      };

      // Save GPS source to IndexedDB for PosterProjectProvider to load
      saveCoordinatesLocal(gpsSource, gpsSourceId);

      // Update project with GPS data
      styledProject.gpsSourceIDs = [gpsSourceId];
      styledProject.gpsData = {
        id: gpsSourceId,
        data: stravaData.gpsData,
      };
      styledProject.name = stravaData.activityName;

      // Clear bounds so the map auto-fits to the new GPS data
      styledProject.bounds = undefined;

      return styledProject;
    },
    [saveCoordinatesLocal]
  );

  // Initialize on mount - uses ref guard to prevent infinite loops from unstable hook dependencies
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    const initializePreview = async () => {
      // Validate template ID
      if (!templateId) {
        setPreviewState({
          status: "error",
          message: "No template selected. Please choose a template first.",
        });
        return;
      }

      // Load preview data from localStorage
      const previewData = getPreviewData();
      if (!previewData) {
        trackEvent(StravaAnalyticsEvents.PREVIEW_ERROR, {
          error_type: StravaPreviewErrorTypes.NO_PREVIEW_DATA,
        });
        setPreviewState({
          status: "error",
          message: "No Strava activity data found. Please connect your Strava account first.",
        });
        return;
      }

      // Load template
      setPreviewState({ status: "loading", message: "Loading template..." });

      try {
        const template = await fetchTemplate(templateId);

        if (!template) {
          trackEvent(StravaAnalyticsEvents.PREVIEW_ERROR, {
            error_type: StravaPreviewErrorTypes.TEMPLATE_NOT_FOUND,
            template_id: templateId,
          });
          setPreviewState({
            status: "error",
            message: "Template not found. Please choose a different template.",
          });
          return;
        }

        // Create project with Strava data
        setPreviewState({ status: "loading", message: "Preparing your poster..." });

        const project = createProjectFromTemplateWithStravaData(template, previewData);

        trackEvent(StravaAnalyticsEvents.PREVIEW_LOADED, {
          template_id: templateId,
          activity_type: previewData.activityType,
          coordinate_count: previewData.coordinates.length,
        });

        setPreviewState({
          status: "ready",
          project,
          template,
          previewData,
        });
      } catch (err) {
        console.error("Failed to load template:", err);
        trackEvent(StravaAnalyticsEvents.PREVIEW_ERROR, {
          error_type: StravaPreviewErrorTypes.LOAD_FAILED,
          error: String(err),
        });
        setPreviewState({
          status: "error",
          message: "Failed to load template. Please try again.",
        });
      }
    };

    initializePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle "Create Account" CTA click.
   */
  const handleCreateAccount = useCallback(() => {
    trackEvent(StravaAnalyticsEvents.PREVIEW_CTA_CLICKED, {
      action: StravaPreviewCTAActions.CREATE_ACCOUNT,
    });
    // Navigate to auth page with stravaFlow flag
    navigateTo(`${auth}?stravaFlow=true`);
  }, [navigateTo, auth, trackEvent]);

  /**
   * Handle "Change Template" link click.
   */
  const handleChangeTemplate = useCallback(() => {
    trackEvent(StravaAnalyticsEvents.PREVIEW_CTA_CLICKED, {
      action: StravaPreviewCTAActions.CHANGE_TEMPLATE,
    });
    // Navigate back to Strava collection
    navigateTo(`${collectionList}/strava`);
  }, [navigateTo, collectionList, trackEvent]);

  /**
   * Handle error retry.
   */
  const handleRetry = useCallback(() => {
    if (previewState.status === "error" && previewState.message.includes("Strava")) {
      // No Strava data - go to home to start OAuth flow
      navigateTo("/");
    } else {
      // Other error - go back to template selection
      navigateTo(`${collectionList}/strava`);
    }
  }, [navigateTo, collectionList, previewState]);

  // Render based on state
  if (previewState.status === "loading") {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContent}>
          <ThreeDot color="var(--color-primary)" size="medium" text="" textColor="" />
          <p className={styles.loadingMessage}>{previewState.message}</p>
        </div>
      </div>
    );
  }

  if (previewState.status === "error") {
    return (
      <div className={styles.container}>
        <div className={styles.errorContent}>
          <div className={styles.errorIcon}>!</div>
          <p className={styles.errorMessage}>{previewState.message}</p>
          <button className={styles.retryButton} onClick={handleRetry}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Ready state - render the preview
  const { project, template, previewData } = previewState;

  return (
    <div className={styles.container}>
      <PosterProjectProvider initialProject={project} projectTemplate={template}>
        <TooltipProvider>
          <EditorUIProvider>
            <div className={styles.previewContainer}>
              <PosterStage
                onShowContextualEditor={() => {}}
                contextType="rendering"
              />
            </div>
          </EditorUIProvider>
        </TooltipProvider>
      </PosterProjectProvider>

      <StravaCtaOverlay
        onCreateAccount={handleCreateAccount}
        onChangeTemplate={handleChangeTemplate}
      />

      <ActivitySummaryCard activity={previewData} />
    </div>
  );
};
